// TemplateDatabase.ts — CRUD for template_settings table + image uploads.
//
// Full column map (from template_inputs.csv):
//
//  tweaks      jsonb  scope:template   — palette, fonts, aesthetic, axes, nav/footer style,
//                                        pages{ order, hidden, cfg{…per-section opts} }, contentEdits
//  bio         jsonb  scope:creator/bio
//  highlights  jsonb  scope:creator/highlights
//  links       jsonb  scope:creator/links
//  banners     jsonb  scope:creator/banners   { hero, promo, footer }
//  carousels   jsonb  scope:creator/carousels
//  products    jsonb  scope:creator/products
//  journal     jsonb  scope:creator/journal
//  press       jsonb  scope:creator/press
//  about       jsonb  scope:creator/about
//  recent      jsonb  scope:creator/recent
//
// Per-page/section inputs (scope: page=X/section=Y) live inside tweaks.pages[X].cfg[Y].

import { supabase } from '../../config/supabaseClient';

const TABLE  = 'template_settings';
const BUCKET = 'template_images';

// ── Row shape (mirrors DB columns) ───────────────────────────
export interface TemplateRow {
  id?:         string;
  user_id:     string;

  // Template-level (palette, fonts, axes, nav, footer, page configs, inline edits)
  tweaks:      Record<string, unknown>;

  // Creator content — each column matches one scope in the CSV
  bio:         Record<string, unknown>;   // CreatorBio
  highlights:  unknown[];                 // HighlightItem[]
  links:       unknown[];                 // LinkItem[]
  banners:     Record<string, unknown>;   // { hero, promo, footer }
  carousels:   unknown[];                 // CarouselData[]
  products:    unknown[];                 // ProductItem[]
  journal:     unknown[];                 // JournalPost[]
  press:       unknown[];                 // PressItem[]
  about:       Record<string, unknown>;   // AboutData
  recent:      unknown[];                 // RecentItem[]

  created_at?: string;
  updated_at?: string;
}

// Subset used for upsert (omit read-only server fields)
export type TemplateUpsert = Partial<Omit<TemplateRow, 'id' | 'created_at' | 'updated_at'>>;

// ── Load ──────────────────────────────────────────────────────
export async function loadTemplateSettings(userId: string): Promise<TemplateRow | null> {
  if (!userId || !userId.trim()) return null;

  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('[TemplateDB] load error:', error.message);
    return null;
  }
  return data as TemplateRow | null;
}

// ── Save — update-then-insert so we never depend on a UNIQUE constraint ───────
export async function saveTemplateSettings(
  userId: string,
  payload: TemplateUpsert,
  insertPayload: TemplateUpsert = payload,
): Promise<{ ok: boolean; error?: string }> {
  if (!userId || !userId.trim()) return { ok: false, error: 'no userId' };

  // 1. Try UPDATE on the existing row
  const { data: updated, error: updateErr } = await supabase
    .from(TABLE)
    .update({ ...payload })
    .eq('user_id', userId)
    .select('id');          // returns the row if it exists

  if (updateErr) {
    console.error('[TemplateDB] update error:', updateErr.message);
    return { ok: false, error: updateErr.message };
  }

  // 2. If no row was updated (first save), INSERT instead
  if (!updated || updated.length === 0) {
    const { error: insertErr } = await supabase
      .from(TABLE)
      .insert({ ...insertPayload, user_id: userId });

    if (insertErr) {
      console.error('[TemplateDB] insert error:', insertErr.message,
        '\n→ Hint: check your table has all required columns and none have NOT NULL without a default.',
        '\n→ Columns sent:', Object.keys({ ...insertPayload, user_id: userId }).join(', '));
      return { ok: false, error: insertErr.message };
    }
  }

  return { ok: true };
}

// ── Image upload ──────────────────────────────────────────────
// Slot naming convention (matches CSV scopes):
//   bio-avatar (website Hero), lib-avatar (Link in Bio profile),
//   banner-hero, banner-promo, banner-footer,
//   highlight-{id}, carousel-{id}-{itemId}, product-{id},
//   journal-{id}, about-headshot, recent-{id}

const UPLOAD_MAX_EDGE_PX = 2400;
const UPLOAD_JPEG_QUALITY = 0.85;
const UPLOAD_COMPRESS_MIN_BYTES = 1.2 * 1024 * 1024;

/**
 * Downscale large phone photos before storage upload so banner/inline replaces
 * finish in seconds instead of hanging on multi‑MB originals.
 */
async function compressImageFileForUpload(file: File): Promise<File> {
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") {
    return file;
  }
  const mime = (file.type || "").toLowerCase();
  if (
    !mime.startsWith("image/") ||
    mime.includes("svg") ||
    mime.includes("gif") ||
    mime.includes("heic") ||
    mime.includes("heif")
  ) {
    return file;
  }
  if (file.size < UPLOAD_COMPRESS_MIN_BYTES) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  try {
    const longest = Math.max(bitmap.width, bitmap.height) || 1;
    const scale = Math.min(1, UPLOAD_MAX_EDGE_PX / longest);
    if (scale >= 1 && file.size < 2.5 * 1024 * 1024) return file;

    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return file;
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);

    // JPEG has no alpha — flattening a PNG onto the default canvas fills
    // transparent pixels with black. Keep WebP/PNG so the page color shows.
    const keepAlpha =
      mime.includes("png") || mime.includes("webp") || mime.includes("avif");
    const blob = await new Promise<Blob | null>((resolve) => {
      if (!keepAlpha) {
        canvas.toBlob(resolve, "image/jpeg", UPLOAD_JPEG_QUALITY);
        return;
      }
      canvas.toBlob((webp) => {
        if (webp) {
          resolve(webp);
          return;
        }
        canvas.toBlob(resolve, "image/png");
      }, "image/webp", UPLOAD_JPEG_QUALITY);
    });
    if (!blob || blob.size <= 0 || blob.size >= file.size) return file;

    const outType = blob.type || (keepAlpha ? "image/webp" : "image/jpeg");
    const ext = outType.includes("png")
      ? "png"
      : outType.includes("webp")
        ? "webp"
        : "jpg";
    const base = (file.name || "image").replace(/\.[^.]+$/, "") || "image";
    return new File([blob], `${base}.${ext}`, {
      type: outType,
      lastModified: Date.now(),
    });
  } finally {
    bitmap.close();
  }
}

export async function uploadTemplateImage(
  userId: string,
  file: File,
  slot: string,
): Promise<{ url: string | null; error?: string }> {
  if (!userId || !userId.trim()) return { url: null, error: 'no userId' };

  const uploadFile = await compressImageFileForUpload(file);
  const ext =
    uploadFile.name.split(".").pop()?.toLowerCase() ||
    (uploadFile.type.includes("png")
      ? "png"
      : uploadFile.type.includes("webp")
        ? "webp"
        : "jpg");
  const path = `${userId}/${slot}-${Date.now()}.${ext}`;

  const { error: uploadErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, uploadFile, { upsert: true, cacheControl: '3600' });

  if (uploadErr) {
    console.error('[TemplateDB] upload error:', uploadErr.message);
    return { url: null, error: uploadErr.message };
  }

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return { url: data.publicUrl };
}

// ── Delete image ──────────────────────────────────────────────
export async function deleteTemplateImage(storagePath: string): Promise<boolean> {
  const { error } = await supabase.storage.from(BUCKET).remove([storagePath]);
  return !error;
}

const USER_TABLE = 'user';


export async function updateUserBrowsableTemplateActivityAt(
  userId: string,
): Promise<{ ok: boolean; at?: string; error?: string }> {
  if (!userId?.trim()) return { ok: false, error: 'no userId' };

  const at = new Date().toISOString();
  const { error } = await supabase
    .from(USER_TABLE)
    .update({ browsable_template_editing_started_at: at })
    .eq('user_id', userId);

  if (error) {
    console.error('[TemplateDB] browsable_template_editing_started_at:', error.message);
    return { ok: false, error: error.message };
  }

  return { ok: true, at };
}

/** Stamp last website Publish click on `user.browsable_website_published_at`. */
export async function updateUserBrowsableWebsitePublishedAt(
  userId: string,
  at = new Date().toISOString(),
): Promise<{ ok: boolean; at?: string; error?: string }> {
  if (!userId?.trim()) return { ok: false, error: 'no userId' };

  const { error } = await supabase
    .from(USER_TABLE)
    .update({ browsable_website_published_at: at })
    .eq('user_id', userId);

  if (error) {
    console.error('[TemplateDB] browsable_website_published_at:', error.message);
    return { ok: false, error: error.message };
  }

  return { ok: true, at };
}
